<script lang="ts">import FieldLabel from '../FieldLabel/FieldLabel.svelte';
import SelectDropdown from '../SelectDropdown/SelectDropdown.svelte';
import ValidationMessage from '../ValidationMessage/ValidationMessage.svelte';
// Need to use let because invalid and inputValue are bindable and you cannot bind to a constant and you cannot use
// the $props() rune twice
/* eslint-disable prefer-const */
let { disabled = false, id = undefined, name = '', required = false, validationMessage = undefined, colorMode = 'light', invalid = $bindable(false), label = '', items = [], stripDelimiters = false, 
// Setting this prop doesn't do anything, but exported so consumer can get it to do validation as needed
inputValue = $bindable(''), class: className = '', ...rest } = $props();
/* eslint-enable prefer-const */
let dropdownIsOpen = $state(false);
let dropdownOpenedByKeyboard = $state(false);
let inputElement;
let rawInputValue = $state('');
let selectedIndex = $state(0);
$effect(() => {
    if (items.length > 0) {
        const foundIndex = items.findIndex((item) => item.selected);
        selectedIndex = foundIndex === -1 ? 0 : foundIndex;
    }
});
const selectedItem = $derived.by(() => {
    if (items.length > 0 && selectedIndex >= 0 && selectedIndex < items.length) {
        return items[selectedIndex];
    }
    return undefined;
});
$effect(() => {
    inputValue = (stripDelimiters ? rawInputValue?.replace(/[\s()-.]/g, '') : rawInputValue) ?? '';
});
const fullPhoneNumber = $derived(`${selectedItem?.value}${inputValue}`);
// Handle keydown events on the input trigger.
function handleCountrySelectKeydown(e) {
    switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case ' ': {
            e.preventDefault();
            dropdownOpenedByKeyboard = true;
            dropdownIsOpen = true;
            break;
        }
        case 'Escape':
        case 'Tab': {
            dropdownIsOpen = false;
            break;
        }
        default: {
            break;
        }
    }
}
function handleCountrySelectClick(e) {
    e.stopPropagation();
    dropdownOpenedByKeyboard = false;
    dropdownIsOpen = !dropdownIsOpen;
}
function handleDropdownSelectOrExit() {
    dropdownIsOpen = false;
    inputElement.focus();
}
function handleOutsideSelectClick() {
    dropdownIsOpen = false;
}
</script>

<svelte:window onclick={handleOutsideSelectClick} />

<div {...rest} class="PhoneField {className}" data-color-mode={colorMode}>
  {#if label}
    <FieldLabel for={name} {disabled} text={label} />
  {/if}

  <div class="PhoneField__container" aria-invalid={invalid}>
    <button
      class="PhoneField__button"
      type="button"
      aria-expanded={dropdownIsOpen}
      onclick={handleCountrySelectClick}
      onkeydown={handleCountrySelectKeydown}
      {disabled}
    >
      {#if selectedItem?.icon}
        <span class="PhoneField__flag">
          <selectedItem.icon />
        </span>
      {/if}

      <svg class="PhoneField__arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8.70703 11.7071C8.31651 12.0976 7.68334 12.0976 7.29282 11.7071L1.29282 5.70711L2.70703 4.29289L7.99992 9.58579L13.2928 4.29289L14.707 5.70711L8.70703 11.7071Z"
        />
      </svg>
    </button>

    <input
      type="tel"
      autocomplete="tel"
      bind:value={rawInputValue}
      {disabled}
      {required}
      id={id || name}
      inputmode="tel"
      placeholder={selectedItem?.placeholder}
      aria-invalid={invalid}
      aria-describedby={invalid ? `phonefield-error-${name}` : undefined}
      title={validationMessage || undefined}
      bind:this={inputElement}
    />
  </div>

  <SelectDropdown
    bind:selectedIndex
    isOpen={dropdownIsOpen}
    {items}
    {name}
    showItemValuesInLabel={true}
    defaultFocusFirstOption={dropdownOpenedByKeyboard}
    {colorMode}
    onSelect={handleDropdownSelectOrExit}
    onExit={handleDropdownSelectOrExit}
  />

  <input type="hidden" value={fullPhoneNumber} {name} />

  {#if validationMessage && invalid}
    <ValidationMessage text={validationMessage} {colorMode} id="phonefield-error-{name}" />
  {/if}
</div>

<style>.PhoneField input {
  font-weight: var(--form-field-input-font-weight);
  font-size: var(--form-field-input-font-size);
  letter-spacing: var(--form-field-input-letter-spacing);
  line-height: var(--form-field-input-line-height);
}

/**
 * Checkbox styling is shared between CheckboxesField and MultiSelectField
 * The biggest difference between the two is the hover/focus/etc states are on different elements, so it cannot just be
 * one big mixin and has to be broken up
 */
@layer design-system-components.pwds {
  .PhoneField {
    --color: var(--color-text);
    position: relative;
  }
  .PhoneField:has(input:disabled) {
    pointer-events: none;
  }
  .PhoneField:has(input:disabled) .PhoneField__container {
    --form-field-inset-box-shadow-size: 1px;
    --form-field-inset-box-shadow-color: var(--color-form-field-disabled-box-shadow);
    background-color: var(--color-form-field-disabled-background-color);
  }
  .PhoneField__container {
    --form-field-inset-box-shadow-size: 1px;
    --form-field-inset-box-shadow-color: var(--color-form-field-default);
    box-shadow: inset 0 0 0 var(--form-field-inset-box-shadow-size) var(--form-field-inset-box-shadow-color);
    overflow: clip;
    display: flex;
    align-items: stretch;
    background-color: var(--color-background);
    border-radius: var(--form-field-input-border-radius);
  }
  .PhoneField__container:hover {
    --form-field-inset-box-shadow-size: 1px;
    --form-field-inset-box-shadow-color: var(--color-form-field-hover);
  }
  .PhoneField__container:focus-within {
    --form-field-inset-box-shadow-size: 2px;
    --form-field-inset-box-shadow-color: var(--color-form-field-focus);
  }
  .PhoneField__container[aria-invalid=true] {
    --form-field-inset-box-shadow-color: var(--color-critical);
  }
  .PhoneField__button {
    display: flex;
    align-items: center;
    gap: var(--space-10);
    margin: var(--phone-field-button-margin);
    padding-right: var(--space-10);
    padding-left: var(--space-10);
    border-radius: var(--phone-field-button-border-radius);
    cursor: pointer;
  }
  .PhoneField__button:hover, .PhoneField__button:focus {
    background-color: var(--color-phone-field-button-background-color-hover);
    outline: none;
  }
  .PhoneField__flag {
    display: flex;
    align-items: center;
  }
  .PhoneField__arrow {
    display: inline-block;
    fill: var(--color-form-field-placeholder);
    transition: transform var(--duration-quick) var(--easing-standard);
  }
  [aria-expanded=true] .PhoneField__arrow {
    transform: rotate(180deg);
  }
  .PhoneField input {
    flex-grow: 1;
    padding: var(--form-field-input-padding);
    touch-action: manipulation;
  }
  .PhoneField input::placeholder {
    color: var(--color-form-field-placeholder);
  }
}</style>
